import { Stack } from "@mui/material";
import {
  type ProfileLookup,
  usePublicProfileQuery,
} from "Api/Queries/Social/usePublicProfileQuery";
import { AuthenticatedLayout } from "Components/Layout/AuthenticatedLayout";
import { StyledPageWrapper } from "Components/Shared/StyledComponents";
import { ProfilePaper } from "Pages/PublicProfile/ProfilePaper";
import { Resources, useResource } from "Translations/Resources";
import { appUrl } from "Utils/UrlUtils";

type Props = {
  lookup: ProfileLookup;
};

const getTitle = (
  lookup: ProfileLookup,
  handle: string | null | undefined,
  displayName: string | null | undefined,
  fallback: string,
) => {
  if ("handle" in lookup) {
    return `@${lookup.handle}`;
  }

  if (handle) {
    return `@${handle}`;
  }

  return displayName ?? fallback;
};

/**
 * The public profile inside the standard app layout — what signed-in viewers get on
 * /profile/{key} (anonymous visitors get the bare PublicProfilePage; the split lives in
 * Routes/PublicProfileRoute). Titled by the handle when the profile has one, else the
 * display name; the query dedupes with ProfilePaper's identical key.
 */
export const AuthenticatedPublicProfilePage = ({ lookup }: Props) => {
  const { t } = useResource();
  const { data } = usePublicProfileQuery(lookup);

  const title = getTitle(
    lookup,
    data?.handle,
    data?.displayName,
    t(Resources.Pages.Titles.Profile),
  );

  return (
    <AuthenticatedLayout
      title={title}
      backNavigation={{ mode: "historyStack", fallbackUrl: appUrl("people") }}
    >
      <StyledPageWrapper>
        <Stack className="left-content" alignItems="center" mb={6}>
          <ProfilePaper lookup={lookup} />
        </Stack>
      </StyledPageWrapper>
    </AuthenticatedLayout>
  );
};
